/**
 * Owns: store inventory API, validation, PDP element resolver (getStoreProductAvailability).
 * Exposes: getStoreProductAvailability, isStoreInventoryProductPage, validateCartLineQuantity, getStoreInventoryConfig.
 * Consumes: store-pricing (cart attributes, catalog).
 */

import {
  fetchCartAttributes,
  findStoreFromCartAttributes,
  hasStoreCartAttributes,
  loadStoreCatalog,
  normalizeLocationId,
} from '@theme/store-pricing';

export { fetchCartAttributes, findStoreFromCartAttributes, hasStoreCartAttributes, loadStoreCatalog };

const USE_MOCK_STORE_INVENTORY = false;

/** @type {number} TODO: adjust for testing in-stock / low / out-of-stock states */
const MOCK_AVAILABLE_QUANTITY = 10;

const MOCK_INVENTORY_DELAY_MS = 200;

/**
 * @returns {boolean}
 */
export function isStoreInventoryMockEnabled() {
  return USE_MOCK_STORE_INVENTORY;
}

const DEFAULT_INVENTORY_THRESHOLD = 10;

const STORE_INVENTORY_API_PATH = '/api/shopify/variant-location-availability';

/**
 * @typedef {Object} StoreInventoryConfig
 * @property {string} baseUrl - Trimmed middleware base (empty = API skipped, Shopify fallback only)
 * @property {string} authToken - Bearer token for middleware (empty = no Authorization header)
 * @property {number} threshold - Low-stock threshold for resolveDeliveryStatus
 */

/**
 * @returns {{ store_inventory_api_base_url?: string; store_inventory_api_auth_token?: string }}
 */
function getThemeStoreInventorySettings() {
  return /** @type {typeof Theme & { settings?: { store_inventory_api_base_url?: string; store_inventory_api_auth_token?: string } }} */ (
    Theme
  ).settings ?? {};
}

/** @type {string} */
export const STORE_PRODUCT_AVAILABILITY_SELECTOR = 'store-product-availability';

/**
 * Builds a query selector for a store-inventory PDP availability element.
 *
 * @param {string} [productId] - When set, scopes to this product; otherwise first enabled element on the page.
 * @returns {string}
 */
export function buildStoreProductAvailabilitySelector(productId) {
  const base = `${STORE_PRODUCT_AVAILABILITY_SELECTOR}[data-store-inventory-enabled="true"]`;
  const trimmed = productId?.trim();
  if (!trimmed) return base;
  return `${base}[data-product-id="${CSS.escape(trimmed)}"]`;
}

/**
 * Resolves the store-product-availability custom element for a PDP.
 *
 * @param {string} [productId] - Product id from product-form / delivery-zip context; omit for first match.
 * @returns {HTMLElement | null}
 */
export function getStoreProductAvailability(productId) {
  const element = document.querySelector(buildStoreProductAvailabilitySelector(productId));
  return element instanceof HTMLElement ? element : null;
}

/**
 * @param {string} productId
 * @returns {boolean}
 */
export function isStoreInventoryProductPage(productId) {
  return Boolean(getStoreProductAvailability(productId));
}

/**
 * @param {string} productId
 * @returns {HTMLElement | null}
 */
export function getProductFormComponent(productId) {
  const trimmed = productId?.trim();
  if (!trimmed) return null;
  const element = document.querySelector(
    `product-form-component[data-product-id="${CSS.escape(trimmed)}"]`
  );
  return element instanceof HTMLElement ? element : null;
}

/**
 * @returns {HTMLElement | null}
 */
function discoverStoreProductAvailabilityElement() {
  return getStoreProductAvailability();
}

/**
 * @param {HTMLElement | null | undefined} element
 * @returns {string}
 */
function readInventoryApiBaseFromElement(element) {
  if (!(element instanceof HTMLElement)) return '';
  return element.dataset.inventoryApiBase?.trim() ?? '';
}

/**
 * @param {HTMLElement | null | undefined} element
 * @returns {number | null}
 */
function readInventoryThresholdFromElement(element) {
  if (!(element instanceof HTMLElement)) return null;
  const parsed = Number.parseInt(element.dataset.inventoryThreshold ?? '', 10);
  if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
  return null;
}

/**
 * @param {HTMLElement | null | undefined} element
 * @returns {string}
 */
function readInventoryAuthTokenFromElement(element) {
  if (!(element instanceof HTMLElement)) return '';
  return element.dataset.inventoryApiAuthToken?.trim() ?? '';
}

/**
 * Resolves middleware inventory API base URL.
 * Precedence: source dataset → Theme.settings → discovered store-product-availability dataset.
 *
 * @param {HTMLElement | null | undefined} [source]
 * @returns {string}
 */
export function getStoreInventoryApiBaseUrl(source) {
  const fromSource = readInventoryApiBaseFromElement(source);
  if (fromSource) return fromSource;

  const fromTheme = String(getThemeStoreInventorySettings().store_inventory_api_base_url ?? '').trim();
  if (fromTheme) return fromTheme;

  return readInventoryApiBaseFromElement(discoverStoreProductAvailabilityElement());
}

/**
 * Resolves middleware inventory API bearer token.
 * Precedence: source dataset → Theme.settings → discovered store-product-availability dataset.
 *
 * @param {HTMLElement | null | undefined} [source]
 * @returns {string}
 */
export function getStoreInventoryApiAuthToken(source) {
  const fromSource = readInventoryAuthTokenFromElement(source);
  if (fromSource) return fromSource;

  const fromTheme = String(getThemeStoreInventorySettings().store_inventory_api_auth_token ?? '').trim();
  if (fromTheme) return fromTheme;

  return readInventoryAuthTokenFromElement(discoverStoreProductAvailabilityElement());
}

/**
 * @param {HTMLElement | null | undefined} [source]
 * @returns {number}
 */
function resolveStoreInventoryThreshold(source) {
  const fromSource = readInventoryThresholdFromElement(source);
  if (fromSource != null) return fromSource;

  const fromDiscovered = readInventoryThresholdFromElement(discoverStoreProductAvailabilityElement());
  if (fromDiscovered != null) return fromDiscovered;

  return DEFAULT_INVENTORY_THRESHOLD;
}

/**
 * @param {HTMLElement | null | undefined} [source] - Element with data-inventory-api-base / data-inventory-threshold
 * @returns {StoreInventoryConfig}
 */
export function getStoreInventoryConfig(source) {
  return {
    baseUrl: getStoreInventoryApiBaseUrl(source),
    authToken: getStoreInventoryApiAuthToken(source),
    threshold: resolveStoreInventoryThreshold(source),
  };
}

/**
 * @returns {number}
 */
export function getStoreInventoryThreshold() {
  return getStoreInventoryConfig().threshold;
}

/**
 * @typedef {'no_store' | 'in_stock' | 'low' | 'out_of_stock' | 'unavailable' | 'loading'} StoreInventoryStatus
 */

/**
 * @typedef {Object} ResolvedStoreInventory
 * @property {StoreInventoryStatus} status
 * @property {boolean} canAddToCart
 * @property {boolean} hasStoreSelected
 * @property {number | null} quantity
 * @property {boolean} showCount
 */

/**
 * @typedef {Object} StoreInventoryApiResult
 * @property {boolean} ok
 * @property {number | null} quantity
 * @property {string} [error]
 */

/**
 * @param {string | number | undefined | null} variantId
 * @returns {string}
 */
export function normalizeVariantId(variantId) {
  return normalizeLocationId(variantId == null ? '' : String(variantId));
}

/**
 * @param {string} baseUrl
 * @param {string} variantId
 * @param {string} locationId
 * @returns {string | null}
 */
export function buildStoreInventoryUrl(baseUrl, variantId, locationId) {
  const base = String(baseUrl ?? '').trim().replace(/\/$/, '');
  const variant = normalizeVariantId(variantId);
  const location = normalizeLocationId(locationId);

  if (!base || !variant || !location) return null;

  try {
    const url = base.startsWith('http')
      ? new URL(`${base}${STORE_INVENTORY_API_PATH}`)
      : new URL(`${base}${STORE_INVENTORY_API_PATH}`, window.location.origin);
    url.searchParams.set('variantId', variant);
    url.searchParams.set('locationId', location);
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * @param {number | null | undefined} quantity
 * @param {number} threshold
 * @returns {StoreInventoryStatus}
 */
export function resolveDeliveryStatus(quantity, threshold) {
  if (quantity == null || Number.isNaN(quantity)) return 'unavailable';
  if (quantity <= 0) return 'out_of_stock';
  if (quantity <= threshold) return 'low';
  return 'in_stock';
}

/**
 * @param {StoreInventoryStatus} status
 * @returns {boolean}
 */
export function canAddToCartForDelivery(status) {
  return status === 'in_stock' || status === 'low';
}

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
export function parseInStockInStoreValues(raw) {
  if (Array.isArray(raw)) {
    return raw.map((entry) => String(entry));
  }

  const str = String(raw ?? '').trim();
  if (!str) return [];

  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed.map((entry) => String(entry));
    }
  } catch {
    /* ignore malformed JSON */
  }

  return [];
}

/**
 * @param {HTMLElement | null | undefined} element
 * @returns {{ inventoryPolicy: string; inStockInStoreValues: string[] }}
 */
export function readContinueSellingSignalsFromElement(element) {
  if (!(element instanceof HTMLElement)) {
    return { inventoryPolicy: '', inStockInStoreValues: [] };
  }

  return {
    inventoryPolicy: element.dataset.inventoryPolicy?.trim() ?? '',
    inStockInStoreValues: parseInStockInStoreValues(element.dataset.inStockInStore),
  };
}

/**
 * @param {Object} params
 * @param {string} [params.inventoryPolicy]
 * @param {unknown} [params.inStockInStoreValues]
 * @param {string} [params.storeCode]
 * @returns {boolean}
 */
export function allowsContinueSellingAtStore({ inventoryPolicy, inStockInStoreValues, storeCode }) {
  if (String(inventoryPolicy ?? '').trim() === 'continue') {
    return true;
  }

  const code = String(storeCode ?? '').trim();
  if (!code) return false;

  const matchToken = `#${code}`;
  const values = parseInStockInStoreValues(inStockInStoreValues);

  return values.some((entry) => String(entry).includes(matchToken));
}

/**
 * When continue-selling applies, treat blocked store-level OOS as sellable without a qty cap.
 *
 * @param {ResolvedStoreAvailability} availability
 * @param {boolean} allowContinue
 * @returns {ResolvedStoreAvailability}
 */
export function applyContinueSellingOverride(availability, allowContinue) {
  if (!allowContinue || canAddToCartForDelivery(availability.deliveryStatus)) {
    return availability;
  }

  return {
    ...availability,
    deliveryStatus: 'in_stock',
    deliveryAvailable: true,
    pickupAvailable: availability.pickUpEnabled,
    quantity: null,
  };
}

/**
 * @typedef {Object} ResolvedStoreAvailability
 * @property {StoreInventoryStatus} deliveryStatus
 * @property {boolean} deliveryAvailable
 * @property {boolean} pickupAvailable
 * @property {boolean} pickUpEnabled
 * @property {number | null} quantity
 */

/**
 * @typedef {Object} StoreLocatorBadgeState
 * @property {boolean} inStock
 * @property {boolean} pickup
 * @property {StoreInventoryStatus} status
 * @property {boolean} [pickUpEnabled]
 */

/**
 * @typedef {Object} StoreLocatorPickupInfo
 * @property {boolean} pickUpEnabled
 */

/**
 * @typedef {Object} StoreLocatorAvailabilityEntry
 * @property {boolean} available
 * @property {boolean} pickUpEnabled
 */

/**
 * @param {number | null | undefined} quantity
 * @param {number} threshold
 * @param {boolean} pickUpEnabled
 * @returns {ResolvedStoreAvailability}
 */
export function resolveStoreAvailability(quantity, threshold, pickUpEnabled) {
  const deliveryStatus = resolveDeliveryStatus(quantity, threshold);
  const deliveryAvailable = canAddToCartForDelivery(deliveryStatus);
  const pickupEnabled = Boolean(pickUpEnabled);

  return {
    deliveryStatus,
    deliveryAvailable,
    pickupAvailable: deliveryAvailable,
    pickUpEnabled: pickupEnabled,
    quantity: quantity ?? null,
  };
}

/**
 * @param {string | undefined | null} rawJson
 * @returns {{ pickUpByLocationId: Record<string, StoreLocatorPickupInfo>; availabilitiesByLocationId: Record<string, StoreLocatorAvailabilityEntry> }}
 */
export function parseStoreLocatorAvailabilities(rawJson) {
  /** @type {Record<string, StoreLocatorPickupInfo>} */
  const pickUpByLocationId = {};
  /** @type {Record<string, StoreLocatorAvailabilityEntry>} */
  const availabilitiesByLocationId = {};

  const raw = String(rawJson ?? '').trim();
  if (!raw) {
    return { pickUpByLocationId, availabilitiesByLocationId };
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return { pickUpByLocationId, availabilitiesByLocationId };
    }

    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const locationId = String(/** @type {{ locationId?: string }} */ (entry).locationId ?? '').trim();
      if (!locationId) continue;

      const pickUpEnabled = Boolean(/** @type {{ pickUpEnabled?: boolean }} */ (entry).pickUpEnabled);
      pickUpByLocationId[locationId] = { pickUpEnabled };
      availabilitiesByLocationId[locationId] = {
        available: Boolean(/** @type {{ available?: boolean }} */ (entry).available),
        pickUpEnabled,
      };
    }
  } catch {
    /* ignore malformed JSON */
  }

  return { pickUpByLocationId, availabilitiesByLocationId };
}

/**
 * @param {string | undefined | null} locationId
 * @param {Record<string, StoreLocatorAvailabilityEntry>} availabilitiesByLocationId
 * @returns {StoreLocatorAvailabilityEntry | null}
 */
export function getStoreAvailabilityEntryForLocation(locationId, availabilitiesByLocationId) {
  if (!locationId) return null;

  const shopifyId = String(locationId).trim();
  const normalized = normalizeLocationId(shopifyId);

  return (
    availabilitiesByLocationId[shopifyId] ??
    availabilitiesByLocationId[normalized] ??
    Object.entries(availabilitiesByLocationId).find(([key]) => normalizeLocationId(key) === normalized)?.[1] ??
    null
  );
}

/**
 * @param {string | undefined | null} locationId
 * @param {Record<string, StoreLocatorPickupInfo>} pickUpByLocationId
 * @returns {boolean}
 */
export function getPickUpEnabledForLocation(locationId, pickUpByLocationId) {
  if (!locationId) return false;

  const shopifyId = String(locationId).trim();
  const normalized = normalizeLocationId(shopifyId);

  const entry =
    pickUpByLocationId[shopifyId] ??
    pickUpByLocationId[normalized] ??
    Object.entries(pickUpByLocationId).find(([key]) => normalizeLocationId(key) === normalized)?.[1];

  return Boolean(entry?.pickUpEnabled);
}

/**
 * @param {StoreLocatorAvailabilityEntry | null | undefined} entry
 * @returns {ResolvedStoreAvailability}
 */
export function resolveStoreAvailabilityFromShopifyEntry(entry) {
  if (!entry) {
    return resolveStoreAvailability(null, 0, false);
  }

  const pickUpEnabled = Boolean(entry.pickUpEnabled);

  if (!entry.available) {
    return {
      deliveryStatus: 'out_of_stock',
      deliveryAvailable: false,
      pickupAvailable: false,
      pickUpEnabled,
      quantity: null,
    };
  }

  return {
    deliveryStatus: 'in_stock',
    deliveryAvailable: true,
    pickupAvailable: true,
    pickUpEnabled,
    quantity: null,
  };
}

/**
 * @typedef {ResolvedStoreAvailability & { fromShopify: boolean }} ResolvedStoreInventoryForLocation
 */

/**
 * @param {Object} params
 * @param {string} params.baseUrl
 * @param {string} params.variantId
 * @param {string} params.locationId
 * @param {Record<string, StoreLocatorAvailabilityEntry>} params.availabilitiesByLocationId
 * @param {number} params.threshold
 * @param {string} [params.authToken]
 * @param {boolean} [params.pickUpEnabled]
 * @param {boolean} [params.allowContinueSelling]
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<ResolvedStoreInventoryForLocation>}
 */
export async function resolveStoreInventoryForLocation({
  baseUrl,
  authToken = '',
  variantId,
  locationId,
  availabilitiesByLocationId,
  threshold,
  pickUpEnabled = false,
  allowContinueSelling = false,
  signal,
}) {
  const trimmedBase = String(baseUrl ?? '').trim();
  /** @type {ResolvedStoreInventoryForLocation} */
  let result;

  if (trimmedBase) {
    const apiResult = await fetchStoreInventory({
      baseUrl: trimmedBase,
      authToken,
      variantId,
      locationId,
      signal,
    });

    if (apiResult.ok && apiResult.quantity != null) {
      result = {
        ...resolveStoreAvailability(apiResult.quantity, threshold, pickUpEnabled),
        fromShopify: false,
      };
    }
  }

  if (!result) {
    const shopifyEntry = getStoreAvailabilityEntryForLocation(locationId, availabilitiesByLocationId);
    if (shopifyEntry) {
      result = {
        ...resolveStoreAvailabilityFromShopifyEntry(shopifyEntry),
        fromShopify: true,
      };
    } else {
      result = {
        ...resolveStoreAvailability(null, threshold, pickUpEnabled),
        fromShopify: false,
      };
    }
  }

  if (allowContinueSelling) {
    return {
      ...applyContinueSellingOverride(result, true),
      fromShopify: result.fromShopify,
    };
  }

  return result;
}

/**
 * @param {ResolvedStoreAvailability} availability
 * @returns {StoreLocatorBadgeState}
 */
export function availabilityToBadgeState(availability) {
  return {
    inStock: availability.deliveryAvailable,
    pickup: availability.pickupAvailable,
    status: availability.deliveryStatus,
    pickUpEnabled: availability.pickUpEnabled,
  };
}

/**
 * @param {number | null | undefined} quantity
 * @param {number} threshold
 * @param {boolean} pickUpEnabled
 * @returns {StoreLocatorBadgeState}
 */
export function resolveStoreLocatorBadges(quantity, threshold, pickUpEnabled) {
  return availabilityToBadgeState(resolveStoreAvailability(quantity, threshold, pickUpEnabled));
}

/**
 * @param {number | null | undefined} quantity
 * @param {number} threshold
 * @param {boolean} showCount
 * @returns {ResolvedStoreInventory}
 */
export function resolveStoreInventoryFromQuantity(quantity, threshold, showCount) {
  const availability = resolveStoreAvailability(quantity, threshold, false);

  return {
    status: availability.deliveryStatus,
    canAddToCart: availability.deliveryAvailable,
    hasStoreSelected: true,
    quantity: availability.quantity,
    showCount,
  };
}

/**
 * @param {string} variantId
 * @param {string} locationId
 * @param {number} available
 * @returns {{ success: true; data: { variantId: string; locationId: string; available: number } }}
 */
function buildMockInventoryPayload(variantId, locationId, available) {
  return {
    success: true,
    data: { variantId, locationId, available },
  };
}

/**
 * @param {unknown} data
 * @returns {number | null}
 */
function parseStoreInventoryResponse(data) {
  if (
    typeof data === 'object' &&
    data != null &&
    'success' in data &&
    data.success === true &&
    'data' in data &&
    typeof data.data === 'object' &&
    data.data != null &&
    'available' in data.data &&
    data.data.available != null
  ) {
    const available = Number(data.data.available);
    return Number.isNaN(available) ? null : available;
  }

  return null;
}

/**
 * @param {number} ms
 * @param {AbortSignal | undefined} signal
 * @returns {Promise<void>}
 */
function waitForMockDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(resolve, ms);

    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

/**
 * @param {Object} params
 * @param {string} params.baseUrl
 * @param {string} [params.authToken]
 * @param {string} params.variantId
 * @param {string} params.locationId
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<StoreInventoryApiResult>}
 */
export async function fetchStoreInventory({ baseUrl, authToken = '', variantId, locationId, signal }) {
  const normalizedVariantId = normalizeVariantId(variantId);
  const normalizedLocationId = normalizeLocationId(locationId);

  if (!normalizedVariantId || !normalizedLocationId) {
    return { ok: false, quantity: null, error: 'invalid_params' };
  }

  if (USE_MOCK_STORE_INVENTORY) {
    try {
      await waitForMockDelay(MOCK_INVENTORY_DELAY_MS, signal);

      const payload = buildMockInventoryPayload(
        normalizedVariantId,
        normalizedLocationId,
        MOCK_AVAILABLE_QUANTITY
      );
      const quantity = parseStoreInventoryResponse(payload);

      if (quantity == null) {
        return { ok: false, quantity: null, error: 'invalid_response' };
      }

      return { ok: true, quantity };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return { ok: false, quantity: null, error: 'aborted' };
      }
      return { ok: false, quantity: null, error: 'network' };
    }
  }

  const url = buildStoreInventoryUrl(baseUrl, variantId, locationId);
  if (!url) {
    return { ok: false, quantity: null, error: 'invalid_params' };
  }

  const trimmedAuthToken = String(authToken ?? '').trim();

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...(trimmedAuthToken ? { Authorization: `Bearer ${trimmedAuthToken}` } : {}),
      },
      signal,
    });

    if (!response.ok) {
      return { ok: false, quantity: null, error: `http_${response.status}` };
    }

    const data = await response.json();
    const quantity = parseStoreInventoryResponse(data);

    if (quantity == null) {
      return { ok: false, quantity: null, error: 'invalid_response' };
    }

    return { ok: true, quantity };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, quantity: null, error: 'aborted' };
    }
    return { ok: false, quantity: null, error: 'network' };
  }
}

/**
 * @param {Object} params
 * @param {string} params.baseUrl
 * @param {string} [params.authToken]
 * @param {string} params.variantId
 * @param {Record<string, string>} params.cartAttributes
 * @param {import('@theme/store-pricing').StoreCatalogEntry[]} params.catalog
 * @param {number} params.threshold
 * @param {boolean} params.showCount
 * @param {AbortSignal} [params.signal]
 * @param {string} [params.storeLocatorAvailabilities] - Serialized variant.store_availabilities from cart line (render-time).
 * @param {string} [params.inventoryPolicy]
 * @param {unknown} [params.inStockInStoreValues]
 * @returns {Promise<ResolvedStoreInventory>}
 */
export async function resolveStoreInventoryForCart({
  baseUrl,
  authToken = '',
  variantId,
  cartAttributes,
  catalog,
  threshold,
  showCount,
  signal,
  storeLocatorAvailabilities,
  inventoryPolicy = '',
  inStockInStoreValues,
}) {
  if (!hasStoreCartAttributes(cartAttributes)) {
    return {
      status: 'no_store',
      canAddToCart: false,
      hasStoreSelected: false,
      quantity: null,
      showCount,
    };
  }

  const store = findStoreFromCartAttributes(catalog, cartAttributes);
  const shopifyLocationId = store?.shopifyLocationId?.trim();
  const storeCode = String(cartAttributes?.store_code ?? '').trim();
  const allowContinueSelling = allowsContinueSellingAtStore({
    inventoryPolicy,
    inStockInStoreValues,
    storeCode,
  });

  if (!shopifyLocationId || !variantId?.trim()) {
    if (allowContinueSelling) {
      return {
        status: 'in_stock',
        canAddToCart: true,
        hasStoreSelected: true,
        quantity: null,
        showCount: false,
      };
    }

    return {
      status: 'unavailable',
      canAddToCart: false,
      hasStoreSelected: true,
      quantity: null,
      showCount,
    };
  }

  const { availabilitiesByLocationId, pickUpByLocationId } = parseStoreLocatorAvailabilities(
    storeLocatorAvailabilities
  );
  const pickUpEnabled = getPickUpEnabledForLocation(shopifyLocationId, pickUpByLocationId);

  const availability = await resolveStoreInventoryForLocation({
    baseUrl,
    authToken,
    variantId,
    locationId: shopifyLocationId,
    availabilitiesByLocationId,
    threshold,
    pickUpEnabled,
    allowContinueSelling,
    signal,
  });

  const effectiveShowCount = showCount && !availability.fromShopify && availability.quantity != null;

  return {
    status: availability.deliveryStatus,
    canAddToCart: availability.deliveryAvailable,
    hasStoreSelected: true,
    quantity: availability.quantity,
    showCount: effectiveShowCount,
  };
}

/**
 * Merges Shopify quantity_rule.max with a store-location inventory cap.
 * @param {number | null} shopifyMax
 * @param {number | null} storeMax
 * @returns {number | null}
 */
export function combineQuantityMax(shopifyMax, storeMax) {
  if (storeMax == null) return shopifyMax;
  if (shopifyMax == null) return storeMax;
  return Math.min(shopifyMax, storeMax);
}

/**
 * @param {Object} params
 * @param {string} params.baseUrl
 * @param {string} [params.authToken]
 * @param {string} params.variantId
 * @param {Record<string, string>} params.cartAttributes
 * @param {import('@theme/store-pricing').StoreCatalogEntry[]} params.catalog
 * @param {number} params.requestedQuantity
 * @param {number} params.threshold
 * @param {AbortSignal} [params.signal]
 * @param {string} [params.storeLocatorAvailabilities] - Serialized variant.store_availabilities from cart line (render-time).
 * @param {string} [params.inventoryPolicy]
 * @param {unknown} [params.inStockInStoreValues]
 * @returns {Promise<{ ok: boolean; availableQuantity?: number; skipped?: boolean }>}
 */
export async function validateCartLineQuantity({
  baseUrl,
  authToken = '',
  variantId,
  cartAttributes,
  catalog,
  requestedQuantity,
  threshold,
  signal,
  storeLocatorAvailabilities,
  inventoryPolicy = '',
  inStockInStoreValues,
}) {
  const resolved = await resolveStoreInventoryForCart({
    baseUrl,
    authToken,
    variantId,
    cartAttributes,
    catalog,
    threshold,
    showCount: false,
    signal,
    storeLocatorAvailabilities,
    inventoryPolicy,
    inStockInStoreValues,
  });

  if (!resolved.hasStoreSelected) {
    return { ok: true };
  }

  // No reliable inventory for variant+location (API error, missing data): defer to checkout.
  if (resolved.status === 'unavailable') {
    return { ok: true, skipped: true };
  }

  if (!canAddToCartForDelivery(resolved.status)) {
    return { ok: false, availableQuantity: resolved.quantity ?? 0 };
  }

  if (resolved.quantity != null && requestedQuantity > resolved.quantity) {
    return { ok: false, availableQuantity: resolved.quantity };
  }

  return { ok: true, availableQuantity: resolved.quantity ?? undefined };
}

/**
 * Pre-submit store inventory check for product forms (PDP adapter + collection cards).
 *
 * @param {Object} params
 * @param {HTMLElement | null | undefined} [params.formElement]
 * @param {string} params.variantId
 * @param {number} [params.requestedQuantity]
 * @param {number} [params.cartQuantity]
 * @param {string} [params.storeLocatorAvailabilities]
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{ ok: boolean; availableQuantity?: number; skipped?: boolean; message?: string }>}
 */
export async function validateProductFormAddToCart({
  formElement,
  variantId,
  requestedQuantity = 1,
  cartQuantity = 0,
  storeLocatorAvailabilities,
  signal,
}) {
  const trimmedVariantId = String(variantId ?? '').trim();
  if (!trimmedVariantId) return { ok: true };

  const [cartAttributes, catalog] = await Promise.all([
    fetchCartAttributes(),
    Promise.resolve(loadStoreCatalog()),
  ]);

  if (!hasStoreCartAttributes(cartAttributes)) {
    return { ok: true };
  }

  const availabilities =
    storeLocatorAvailabilities?.trim() ??
    (formElement instanceof HTMLElement ? formElement.dataset.storeLocatorAvailabilities?.trim() : '') ??
    '';

  const continueSignals = readContinueSellingSignalsFromElement(
    formElement instanceof HTMLElement ? formElement : null
  );

  const { baseUrl, authToken, threshold } = getStoreInventoryConfig(formElement ?? undefined);
  const hasMiddleware = Boolean(baseUrl.trim());
  const hasShopifyAvailabilities = availabilities.length > 0;
  const hasContinueSignals =
    continueSignals.inventoryPolicy === 'continue' || continueSignals.inStockInStoreValues.length > 0;

  if (!hasMiddleware && !hasShopifyAvailabilities && !hasContinueSignals) {
    return { ok: true };
  }

  const totalRequested = cartQuantity + requestedQuantity;
  const validation = await validateCartLineQuantity({
    baseUrl,
    authToken,
    variantId: trimmedVariantId,
    cartAttributes,
    catalog,
    requestedQuantity: totalRequested,
    threshold,
    signal,
    storeLocatorAvailabilities: hasShopifyAvailabilities ? availabilities : undefined,
    inventoryPolicy: continueSignals.inventoryPolicy,
    inStockInStoreValues: continueSignals.inStockInStoreValues,
  });

  if (validation.ok) return validation;

  const message = formatStoreInventoryCartQuantityError({
    availableQuantity: validation.availableQuantity,
    revertedQuantity: cartQuantity,
    requestedQuantity: totalRequested,
  });

  return { ...validation, message };
}

/**
 * @typedef {Object} StoreInventoryPickerVariant
 * @property {string | number} id
 * @property {string | null} [option1]
 * @property {string | null} [option2]
 * @property {string | null} [option3]
 * @property {boolean} shopifyAvailable
 * @property {string} [inventoryPolicy]
 * @property {unknown} [inStockInStore]
 * @property {Array<{ locationId: string; available: boolean; pickUpEnabled: boolean }>} [storeAvailabilities]
 */

/**
 * @param {StoreInventoryPickerVariant} variant
 * @param {'option1' | 'option2' | 'option3'} optionKey
 * @returns {string | null | undefined}
 */
function getVariantOptionValue(variant, optionKey) {
  switch (optionKey) {
    case 'option1':
      return variant.option1;
    case 'option2':
      return variant.option2;
    case 'option3':
      return variant.option3;
    default:
      return undefined;
  }
}

/** @type {Map<string, boolean>} */
const variantStoreAvailabilityCache = new Map();

/** Clears cached per-variant store availability (e.g. after store selection changes). */
export function clearVariantStoreAvailabilityCache() {
  variantStoreAvailabilityCache.clear();
}

/**
 * @param {string | number} variantId
 * @param {string} locationId
 * @returns {string}
 */
function getVariantStoreAvailabilityCacheKey(variantId, locationId) {
  return `${String(variantId)}:${normalizeLocationId(locationId)}`;
}

/**
 * @param {Object} params
 * @param {StoreInventoryPickerVariant[]} params.variants
 * @param {Record<string, string>} params.cartAttributes
 * @param {import('@theme/store-pricing').StoreCatalogEntry[]} params.catalog
 * @param {string} params.baseUrl
 * @param {string} [params.authToken]
 * @param {number} params.threshold
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<Map<string, boolean>>}
 */
export async function resolveVariantStoreAvailabilityMap({
  variants,
  cartAttributes,
  catalog,
  baseUrl,
  authToken = '',
  threshold,
  signal,
}) {
  const store = findStoreFromCartAttributes(catalog, cartAttributes);
  const shopifyLocationId = store?.shopifyLocationId?.trim();
  const storeCode = String(cartAttributes?.store_code ?? '').trim();
  /** @type {Map<string, boolean>} */
  const availabilityByVariantId = new Map();

  if (!shopifyLocationId) {
    for (const variant of variants) {
      const variantId = String(variant.id);
      const allowContinueSelling = allowsContinueSellingAtStore({
        inventoryPolicy: variant.inventoryPolicy,
        inStockInStoreValues: variant.inStockInStore,
        storeCode,
      });

      if (allowContinueSelling) {
        availabilityByVariantId.set(variantId, true);
      }
    }

    return availabilityByVariantId;
  }

  await Promise.all(
    variants.map(async (variant) => {
      const variantId = String(variant.id);
      const cacheKey = getVariantStoreAvailabilityCacheKey(variantId, shopifyLocationId);
      const cached = variantStoreAvailabilityCache.get(cacheKey);
      if (cached !== undefined) {
        availabilityByVariantId.set(variantId, cached);
        return;
      }

      const availabilitiesJson = JSON.stringify(variant.storeAvailabilities ?? []);
      const { availabilitiesByLocationId, pickUpByLocationId } =
        parseStoreLocatorAvailabilities(availabilitiesJson);
      const pickUpEnabled = getPickUpEnabledForLocation(shopifyLocationId, pickUpByLocationId);
      const allowContinueSelling = allowsContinueSellingAtStore({
        inventoryPolicy: variant.inventoryPolicy,
        inStockInStoreValues: variant.inStockInStore,
        storeCode,
      });

      const availability = await resolveStoreInventoryForLocation({
        baseUrl,
        authToken,
        variantId,
        locationId: shopifyLocationId,
        availabilitiesByLocationId,
        threshold,
        pickUpEnabled,
        allowContinueSelling,
        signal,
      });

      const isAvailable = canAddToCartForDelivery(availability.deliveryStatus);

      variantStoreAvailabilityCache.set(cacheKey, isAvailable);
      availabilityByVariantId.set(variantId, isAvailable);
    })
  );

  return availabilityByVariantId;
}

/**
 * @param {Object} params
 * @param {StoreInventoryPickerVariant[]} params.variants
 * @param {Map<string, boolean>} params.availabilityByVariantId
 * @param {(string | null)[]} params.selectedOptions - option1, option2, option3
 * @returns {Map<string, boolean>} keys `${position}:${value}`
 */
export function computeOptionAvailabilityForPicker({
  variants,
  availabilityByVariantId,
  selectedOptions,
}) {
  /** @type {Map<string, boolean>} */
  const optionAvailability = new Map();
  /** @type {Array<'option1' | 'option2' | 'option3'>} */
  const optionKeys = ['option1', 'option2', 'option3'];

  optionKeys.forEach((optionKey, position) => {
    /** @type {Set<string>} */
    const values = new Set();

    for (const variant of variants) {
      const value = getVariantOptionValue(variant, optionKey);
      if (value != null && String(value).trim() !== '') {
        values.add(String(value));
      }
    }

    for (const value of values) {
      const isAvailable = variants.some((variant) => {
        if (String(getVariantOptionValue(variant, optionKey) ?? '') !== value) return false;

        for (let other = 0; other < optionKeys.length; other++) {
          if (other === position) continue;
          const otherKey = optionKeys[other];
          if (!otherKey) continue;
          const selected = selectedOptions[other];
          if (selected == null || String(selected).trim() === '') continue;
          if (String(getVariantOptionValue(variant, otherKey) ?? '') !== String(selected)) {
            return false;
          }
        }

        const variantId = String(variant.id);
        const storeAvailable = availabilityByVariantId.get(variantId);
        if (storeAvailable === undefined) {
          return Boolean(variant.shopifyAvailable);
        }
        return storeAvailable;
      });

      optionAvailability.set(`${position + 1}:${value}`, isAvailable);
    }
  });

  return optionAvailability;
}

/**
 * @param {Object} params
 * @param {number | undefined} params.availableQuantity
 * @param {number} params.revertedQuantity
 * @param {number} params.requestedQuantity
 * @returns {string}
 */
export function formatStoreInventoryCartQuantityError({
  availableQuantity,
  revertedQuantity,
  requestedQuantity,
}) {
  const maximum = availableQuantity ?? 0;
  const isQuantityCap =
    availableQuantity != null && availableQuantity > 0 && requestedQuantity > availableQuantity;

  if (!isQuantityCap) {
    return (
      Theme.translations?.store_inventory_cart_not_available ??
      "This item isn't available at your selected store."
    );
  }

  const template =
    Theme.translations?.store_inventory_cart_quantity_kept ??
    'Only {{ maximum }} available at your store. Quantity kept at {{ quantity }}.';

  return template
    .replace(/\{\{\s*maximum\s*\}\}/g, String(maximum))
    .replace(/\{\{\s*quantity\s*\}\}/g, String(revertedQuantity));
}

/**
 * @param {number} attemptedQuantity
 * @param {number | undefined} availableQuantity
 * @param {number} previousQuantity
 * @returns {number}
 */
export function resolveRevertedCartQuantity(attemptedQuantity, availableQuantity, previousQuantity) {
  if (availableQuantity != null && availableQuantity > 0 && attemptedQuantity > availableQuantity) {
    return Math.min(previousQuantity, availableQuantity);
  }

  return previousQuantity;
}

/**
 * @param {Object} params
 * @param {number | undefined} params.availableQuantity
 * @param {number} params.cartQuantity
 * @returns {string}
 */
export function formatStoreInventoryCheckoutLineError({ availableQuantity, cartQuantity }) {
  const maximum = availableQuantity ?? 0;
  const isQuantityCap =
    availableQuantity != null && availableQuantity > 0 && cartQuantity > availableQuantity;

  if (!isQuantityCap) {
    return (
      Theme.translations?.store_inventory_cart_not_available ??
      "This item isn't available at your selected store."
    );
  }

  const template =
    Theme.translations?.store_inventory_checkout_exceeds_available ??
    'Only {{ maximum }} available at your selected store. You have {{ quantity }} in your cart.';

  return template
    .replace(/\{\{\s*maximum\s*\}\}/g, String(maximum))
    .replace(/\{\{\s*quantity\s*\}\}/g, String(cartQuantity));
}

/**
 * @typedef {Object} CheckoutValidationLine
 * @property {number} line - 1-based cart line index
 * @property {string} variantId
 * @property {number} requestedQuantity
 * @property {string} [storeLocatorAvailabilities]
 * @property {string} [inventoryPolicy]
 * @property {unknown} [inStockInStoreValues]
 */

/**
 * @param {Object} params
 * @param {CheckoutValidationLine[]} params.lines
 * @param {Record<string, string>} params.cartAttributes
 * @param {import('@theme/store-pricing').StoreCatalogEntry[]} params.catalog
 * @param {string} params.baseUrl
 * @param {string} [params.authToken]
 * @param {number} params.threshold
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<Array<{ line: number; ok: boolean; availableQuantity?: number; skipped?: boolean }>>}
 */
export async function validateCartLinesForCheckout({
  lines,
  cartAttributes,
  catalog,
  baseUrl,
  authToken = '',
  threshold,
  signal,
}) {
  return Promise.all(
    lines.map(async ({ line, variantId, requestedQuantity, storeLocatorAvailabilities, inventoryPolicy, inStockInStoreValues }) => {
      const validation = await validateCartLineQuantity({
        baseUrl,
        authToken,
        variantId,
        cartAttributes,
        catalog,
        requestedQuantity,
        threshold,
        signal,
        storeLocatorAvailabilities,
        inventoryPolicy,
        inStockInStoreValues,
      });

      return { line, ...validation };
    })
  );
}
